// LocationScopeSelect — one shared location picker used across Roster, Payroll,
// Reports and any page that filters by vestiging.
//
// Behaviour spec (Fase 2, Feb 2026):
//   • 0 locations              → renders a small empty-state hint
//   • 1 location  (auto)       → renders NOTHING (auto-selected upstream)
//   • >1 locations             → renders a compact pill-select
//   • allowAll=true            → adds an "Alle locaties" option (owner default)
//   • allowAll=false           → hides the "Alle" option (scoped managers can't
//                                see foreign locations anyway)
//   • disabled locations that don't exist in `options` are never rendered
//
// The component is presentation-only: it does not read/write global state,
// does not talk to the API, and does not manage its own selection — the
// parent owns that (single source of truth). This keeps it trivially unit-
// testable and safe to use in three unrelated screens.
import { MapPin, Globe2 } from "lucide-react";

/**
 * @param {object} props
 * @param {Array<{_id:string,name:string,color?:string}>} props.locations
 * @param {string} props.value            currently selected id ("" = all)
 * @param {(next:string)=>void} props.onChange
 * @param {boolean} [props.allowAll=true]
 * @param {string}  [props.allLabel="Alle locaties"]
 * @param {string}  [props.emptyLabel="Nog geen locaties"]
 * @param {string}  [props.testid="location-scope-select"]
 * @param {string}  [props.className=""]
 * @param {"pill"|"input"} [props.variant="pill"]  visual style
 * @param {string}  [props.ariaLabel]
 */
export default function LocationScopeSelect({
  locations = [],
  value = "",
  onChange,
  allowAll = true,
  allLabel = "Alle locaties",
  emptyLabel = "Nog geen locaties",
  testid = "location-scope-select",
  className = "",
  variant = "pill",
  ariaLabel = "Locatie",
}) {
  // Rule 1 — no locations at all.
  if (!Array.isArray(locations) || locations.length === 0) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border hairline text-[hsl(var(--text-muted))] text-[12px] font-medium ${className}`}
      >
        <MapPin size={12} strokeWidth={2} />
        {emptyLabel}
      </div>
    );
  }

  // Rule 2 — exactly one location: render nothing. The parent should have
  // auto-selected it. Rendering an empty picker would be noise.
  if (locations.length === 1) {
    return null;
  }

  // Rule 3 — many locations.
  const Icon = value ? MapPin : Globe2;
  const active = locations.find((l) => l._id === value);
  const dot = active?.color;

  if (variant === "input") {
    return (
      <select
        data-testid={testid}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className={`input-field ${className}`}
      >
        {allowAll && <option value="">{allLabel}</option>}
        {locations.map((l) => (
          <option key={l._id} value={l._id}>{l.name}</option>
        ))}
      </select>
    );
  }

  // Default pill.
  return (
    <div
      data-testid={`${testid}-wrap`}
      className={`inline-flex items-center h-9 rounded-full border hairline bg-[hsl(var(--surface))] overflow-hidden ${className}`}
    >
      <span className="pl-3 pr-1.5 text-[hsl(var(--text-muted))] inline-flex items-center gap-1">
        {dot ? (
          <span className="w-2 h-2 rounded-full" style={{ background: dot }} />
        ) : (
          <Icon size={12} strokeWidth={2.2} />
        )}
      </span>
      <select
        data-testid={testid}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className="h-9 pr-3 pl-1 text-[12.5px] font-semibold bg-transparent outline-none border-0 cursor-pointer text-[hsl(var(--text-strong))]"
      >
        {allowAll && <option value="">{allLabel}</option>}
        {locations.map((l) => (
          <option key={l._id} value={l._id}>{l.name}</option>
        ))}
      </select>
    </div>
  );
}
